import React from 'react';
import styled, { css } from 'styled-components';
import { AuthState, withAuth } from 'context/auth';
import { AlertsState } from 'context/alerts';
import {
  Button,
  ContentArea,
  NoVNC,
  LoadingDots,
  PageLoader,
  UnstyledButton,
  CustomVMSlider,
  CustomVMDropdown,
  CustomVMCheckbox,
  VMNotification,
} from 'components';
import { ChevronDown } from 'lucide-react';
import { slideUp, textFocusIn } from 'styles/animations';
import { media } from 'styles/media';
import {
  CustomVMProfile,
  getVM,
  getVMAcceleratorTypes,
  getVMRestrictions,
  getVMMachineTypes,
  getVMProfiles,
  getVMDiskLimits,
  postStartVM,
  putShutdownVM,
  StandardVMProfile,
} from 'utils/api';
import { CustomVMDropdownOption } from 'components/custom-vm-dropdown';
import VirtualMachineControlPanel from '../components/vm-control-panel';
import { NavigateFunction, useNavigate } from 'react-router-dom';

const StyledVirtualMachineSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: ${p => p.theme.spacing.base};
  width: 100%;
`;
const StyledStartVMButton = styled(Button)`
  margin-top: ${p => p.theme.spacing.sm};
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  max-width: 200px;
  color: ${p => p.theme.colors.actionFg};
  border-color: ${p => p.theme.colors.actionFg};

  &:hover,
  &:active,
  &:focus {
    color: ${p => p.theme.colors.surface};
    background-color: ${p => p.theme.colors.actionFg};
  }
`;
const StyledVirtualMachineCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${p => p.theme.spacing.base};
  border-radius: 12px;
  background-color: ${p => p.theme.colors.surfaceElevated};
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease;
  ${slideUp}

  &:hover {
    border-color: ${p => p.theme.colors.midGrey};
    box-shadow:
      0 4px 6px rgba(0, 0, 0, 0.1),
      0 8px 15px rgba(0, 0, 0, 0.08);
  }
`;
const StyledVMName = styled.p`
  font-size: ${p => p.theme.fontSize.xl};
  font-weight: bold;
  margin-top: 0;
  margin-bottom: ${p => p.theme.spacing.sm};
`;
const StyledVMProperties = styled.ul`
  flex: 1;
  margin: ${p => p.theme.spacing.sm} 0;
  padding-left: 28px;
`;
const StyledVMStarting = styled.div`
  margin-top: ${p => p.theme.spacing['2xl']};
  display: flex;
  flex-direction: column;
  text-align: center;
`;
const StyledReconnectText = styled.p`
  ${textFocusIn}
  ${UnstyledButton} {
    margin: 20px 5px;
  }
`;
const StyledShutdownButton = styled(UnstyledButton)`
  margin: 0 5px;
  text-decoration: underline;
  color: ${p => p.theme.colors.deepBlue};
  transition: color 0.2s;
  font: inherit;

  &:hover,
  &:focus {
    color: ${p => p.theme.colors.darkBlue};
  }
`;
const StyledParagraph = styled.p`
  ${slideUp}
`;
const StyledCustomVirtualMachineCard = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
  padding: 0;
  border-radius: 12px;
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  ${slideUp}
`;
const StyledCustomVMProperties = styled.ul`
  display: flex;
  flex-direction: column;
  max-width: 550px;
  width: 100%;
  margin: 15px 0;
  padding: 0 28px;
  ${slideUp}
`;
const StyledCustomVMButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${p => p.theme.spacing.base};
  font-size: ${p => p.theme.fontSize.base};
  font-weight: 600;
  color: ${p => p.theme.colors.black};
  background-color: ${p => p.theme.colors.surfaceElevated};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${p => p.theme.colors.lightGrey};
  }
`;
const StyledAccordionChevron = styled(ChevronDown)<{ $isOpen: boolean }>`
  flex-shrink: 0;
  transition: transform 0.2s ease;
  transform: ${p => (p.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;
const StyledAccordionContent = styled.div`
  padding: ${p => p.theme.spacing.base};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const StyledCustomVMSection = styled.div`
  margin: 5px 0;
`;
const StyledCustomVMPrice = styled.div`
  margin: 0 0 15px 0;
`;
const StyledReconnectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 806px;
  width: 100%;
  padding: ${p => p.theme.spacing.md};
  border-radius: 12px;
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease;
  ${slideUp}

  &:hover {
    border-color: ${p => p.theme.colors.midGrey};
    box-shadow:
      0 4px 6px rgba(0, 0, 0, 0.1),
      0 8px 15px rgba(0, 0, 0, 0.08);
  }
`;
const StyledReconnectTitle = styled.h3`
  margin: 30px 50px;
  font-size: ${p => p.theme.fontSize['4xl']};
  line-height: 1;
  text-align: center;
`;
const StyledReconnectDescription = styled.p`
  text-align: center;
  margin-top: 0;
  margin-bottom: 20px;
  font-size: ${p => p.theme.fontSize.lg};
  max-width: 440px;
  ${slideUp}
`;
const StyledReconnectOptionContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 30px 50px;
  max-width: 700px;
  width: 100%;
  ${slideUp}

  ${media.md} {
    flex-direction: column;
    padding: 24px 16px;
  }

  ${Button} {
    &:not(:last-child) {
      margin-right: 20px;
    }
  }
`;

const RedButtonStyles = css`
  background-color: ${p => p.theme.colors.darkRed};

  :hover,
  :active,
  :focus {
    background-color: ${p => p.theme.colors.deepRed};
  }
`;

const GrayButtonStyles = css`
  background-color: ${p => p.theme.colors.darkGrey};

  :hover,
  :active,
  :focus {
    background-color: ${p => p.theme.colors.deepGrey};
  }
`;

interface VMAllowedFamily {
  name: string;
  description: string;
}

interface VMRestrictions {
  families: VMAllowedFamily[];
  max_cpus: number;
  max_mem_mb: number;
  max_gpus: number;
}

interface VMMachineType {
  name: string;
  description: string;
  quest_cpus: number;
  price_per_hour: number;
  currency: string;
}

interface VMAcceleratorType {
  name: string;
  description: string;
  maximum_cards_per_instance: number;
}

interface VMDiskLimits {
  disk_min_size: number;
  disk_max_size: number;
  disk_current_size: number;
}

interface Props {
  sandbox: Sandbox;
  sandboxId: string;
  auth: AuthState;
  alerts: AlertsState;
}

interface VMType {
  id: number;
  name: string;
  description: string;
  price_per_hour: number;
  currency: string;
}
interface State {
  loadingVM: boolean;
  startingVM: boolean;
  shuttingDownVM: boolean;
  runningVM: boolean;
  vmTypes: VMType[];
  vmAddress: string;
  vmStatus: string;
  vmPassword: string;
  vmOutdated: boolean;
  outdatedConfirmed: boolean;
  lockdownActive: boolean;
  lockdownBypassAllowed: boolean;
  lockdownMessage: string;
  bypassConfirmed: boolean;
  showCustomVMProfile: boolean;
  machineTypeDropdownValue: string;
  machineFamilyDropdownValue: string;
  machineSubtypeDropdownValue: string;
  acceleratorTypeDropdownValue: string;
  acceleratorSliderValue: number;
  machineTypeOptions: CustomVMDropdownOption[];
  machineFamilyOptions: CustomVMDropdownOption[];
  machineSubtypeOptions: CustomVMDropdownOption[];
  acceleratorTypeOptions: CustomVMDropdownOption[];
  availableMachineTypes: VMMachineType[];
  availableAcceleratorTypes: VMAcceleratorType[];
  includeAccelerator: boolean;
  maxAllowedAccelerators: number;
  resizeHomeDisk: boolean;
  homeDiskSizeMinGB: number;
  homeDiskSizeMaxGB: number;
  homeDiskSizeOriginalGB: number;
  homeDiskSizeNewGB: number;
}

const VirtualMachinePage = (props: Props) => {
  const navigate = useNavigate();
  return (
    <VirtualMachinePageClass
      {...props}
      navigate={navigate}
    ></VirtualMachinePageClass>
  );
};

class VirtualMachinePageClass extends React.Component<
  Props & { navigate: NavigateFunction },
  State
> {
  readonly state: State = {
    loadingVM: true,
    startingVM: false,
    shuttingDownVM: false,
    runningVM: false,
    vmTypes: [],
    vmAddress: '',
    vmStatus: '',
    vmPassword: '',
    vmOutdated: false,
    outdatedConfirmed: false,
    lockdownActive: false,
    lockdownBypassAllowed: false,
    lockdownMessage: '',
    bypassConfirmed: false,
    showCustomVMProfile: false,
    machineTypeDropdownValue: '',
    machineFamilyDropdownValue: '',
    machineSubtypeDropdownValue: '',
    acceleratorTypeDropdownValue: '',
    acceleratorSliderValue: 0,
    machineTypeOptions: [],
    machineFamilyOptions: [],
    machineSubtypeOptions: [],
    acceleratorTypeOptions: [],
    availableMachineTypes: [],
    availableAcceleratorTypes: [],
    includeAccelerator: false,
    maxAllowedAccelerators: 0,
    resizeHomeDisk: false,
    homeDiskSizeMinGB: 0,
    homeDiskSizeMaxGB: 0,
    homeDiskSizeOriginalGB: 0,
    homeDiskSizeNewGB: 0,
  };

  private vmTimeout: number = 0;
  private customVMProfileRef = React.createRef<HTMLButtonElement>();
  private resizeHomeDiskRef = React.createRef<HTMLButtonElement>();

  constructor(props: any) {
    super(props);
    this.onDirtyDisconnect = this.onDirtyDisconnect.bind(this);
    this.loadVM = this.loadVM.bind(this);
    this.shutdownVM = this.shutdownVM.bind(this);
  }

  async componentDidMount() {
    this.loadVM();
  }

  /**
   * If VNC connection ends without an ending handshake,
   * check if lockdown was initiated and if not
   * set error message to UI and shutdown the current VM.
   */
  async onDirtyDisconnect(errorMsg: string) {
    try {
      // If server drops VNC connections without end handshake we need to check if this was because lockdown started
      // This is not ideal but implementin proper closing handshake on proxy server would be much harder.
      const vm = await getVM(this.props.auth, this.props.auth.sandboxId);
      if (vm.lockdown_active) {
        this.setState({
          bypassConfirmed: false,
          outdatedConfirmed: false,
        });
        return;
      }
    } catch (err: any) {
      this.props.alerts.setMessage(err, 'danger', true);
    }
    this.setState(
      {
        vmStatus: 'TERMINATED',
      },
      () => {
        this.props.alerts.setMessage({ error: errorMsg }, 'danger', true);
        this.shutdownVM();
      }
    );
  }

  getMachineSubtype(machineTypeName: string): string {
    if (machineTypeName.split('-').length >= 1) {
      const firstIndex = machineTypeName.indexOf('-');
      const lastIndex = machineTypeName.lastIndexOf('-');
      if (lastIndex === firstIndex) {
        return machineTypeName.substring(firstIndex + 1);
      } else {
        return machineTypeName.substring(firstIndex + 1, lastIndex);
      }
    }
    return '';
  }

  /**
   * Check Virtual Machine status.
   * Statuses:
   *  NOT_FOUND = Load and list all available Virtual Machines for user
   *  RUNNING = Virtual Machine and VNC connection are ready
   *  all other statuses = Virtual Machine is ready but the VNC connection is still pending.
   *  Call loadVM() again until connection is established.
   */
  async loadVM() {
    try {
      const vm = await getVM(this.props.auth, this.props.auth.sandboxId);
      this.setState({
        lockdownActive: vm.lockdown_active,
        lockdownBypassAllowed: vm.lockdown_bypass_allowed,
        lockdownMessage: vm.lockdown_message,
      });

      if (vm.vm_status === 'NOT_FOUND') {
        // no vm running
        await this.fetchAvailableVMTypes();
        await this.fetchVMRestrictions();
        await this.fetchAvailableAcceleratorTypes();
        await this.fetchVMDiskLimits();
        this.setState({
          loadingVM: false,
          startingVM: false,
          runningVM: false,
          vmOutdated: false,
        });
        return false;
      } else {
        if (vm.vm_status === 'STOPPING') {
          this.setState({
            runningVM: false,
            vmOutdated: vm.vm_outdated,
          });
        }

        if (vm.vm_status !== 'RUNNING') {
          this.setState({
            vmStatus: vm.vm_status,
            vmOutdated: vm.vm_outdated,
            runningVM: true,
          });
          this.setVMLoadTimer();
        } else {
          // we have a machine
          // use only the first one in the list
          this.setState({
            vmStatus: vm.vm_status,
            vmOutdated: vm.vm_outdated,
            vmPassword: vm.password,
            vmAddress: `${vm.vnc_path}`,
            loadingVM: false,
            startingVM: false,
            runningVM: true,
          });
        }
      }
    } catch (err: any) {
      this.setState(
        {
          loadingVM: false,
        },
        () => this.props.alerts.setMessage(err, 'danger', true)
      );
    }
  }

  /**
   * Poll virtual machine status so that we know when the VNC connection is ready
   */
  setVMLoadTimer() {
    this.vmTimeout = window.setTimeout(() => this.loadVM(), 2000);
  }

  async fetchAvailableVMTypes() {
    try {
      const vmTypes = await getVMProfiles(
        this.props.auth,
        this.props.auth.sandboxId
      );
      this.setState({
        vmTypes,
      });
    } catch (err: any) {
      this.props.alerts.setMessage(err, 'danger', true);
    }
  }

  async fetchVMRestrictions() {
    try {
      const vmRestrictions: VMRestrictions = await getVMRestrictions(
        this.props.auth,
        this.props.auth.sandboxId
      );
      const options: CustomVMDropdownOption[] = vmRestrictions.families.map(
        (family: VMAllowedFamily) => ({
          name: `${family.name} (${family.description})`,
          value: family.name,
        })
      );
      this.setState({
        machineFamilyOptions: options,
        machineFamilyDropdownValue: '',
        machineSubtypeOptions: [],
        machineSubtypeDropdownValue: '',
        machineTypeOptions: [],
        machineTypeDropdownValue: '',
        includeAccelerator: false,
        acceleratorTypeDropdownValue: '',
        acceleratorSliderValue: 0,
        maxAllowedAccelerators: vmRestrictions.max_gpus,
      });
    } catch (err: any) {
      this.props.alerts.setMessage(err, 'danger', true);
    }
  }

  async fetchAvailableMachineTypes(machineFamily: string) {
    try {
      const availableMachineTypes: VMMachineType[] = await getVMMachineTypes(
        this.props.auth,
        this.props.auth.sandboxId,
        machineFamily
      );

      let subtypes = new Set<string>();
      for (let i = 0; i < availableMachineTypes.length; i++) {
        subtypes.add(this.getMachineSubtype(availableMachineTypes[i].name));
      }

      const options: CustomVMDropdownOption[] = Array.from(subtypes).map(
        (subtype: string) => ({
          name: subtype,
          value: subtype,
        })
      );
      if (options.length === 0) {
        this.setState({
          machineSubtypeOptions: options,
          machineTypeOptions: [],
        });
      }
      this.setState({
        machineSubtypeOptions: options,
        availableMachineTypes: availableMachineTypes,
        machineSubtypeDropdownValue: '',
        machineTypeDropdownValue: '',
      });
    } catch (err: any) {
      this.props.alerts.setMessage(err, 'danger', true);
    }
  }

  async fetchAvailableAcceleratorTypes() {
    try {
      const availableAcceleratorTypes = await getVMAcceleratorTypes(
        this.props.auth,
        this.props.auth.sandboxId
      );
      const acceleratorTypeOptions: CustomVMDropdownOption[] =
        availableAcceleratorTypes.map((acceleratorType: VMAcceleratorType) => ({
          name: acceleratorType.description,
          value: acceleratorType.name,
        }));
      this.setState({
        acceleratorTypeOptions: acceleratorTypeOptions,
        availableAcceleratorTypes: availableAcceleratorTypes,
      });
    } catch (err: any) {
      this.props.alerts.setMessage(err, 'danger', true);
    }
  }

  async fetchVMDiskLimits() {
    try {
      const vmDiskLimits: VMDiskLimits = await getVMDiskLimits(
        this.props.auth,
        this.props.auth.sandboxId
      );
      this.setState({
        homeDiskSizeMinGB: vmDiskLimits.disk_min_size,
        homeDiskSizeMaxGB: vmDiskLimits.disk_max_size,
        homeDiskSizeOriginalGB: vmDiskLimits.disk_current_size,
        homeDiskSizeNewGB: vmDiskLimits.disk_current_size,
      });
    } catch (err: any) {
      this.props.alerts.setMessage(err, 'danger', true);
    }
  }

  async startVM(vmProfile: StandardVMProfile | CustomVMProfile) {
    this.setState(
      {
        startingVM: true,
      },
      () => this.props.alerts.clearMessage()
    );
    try {
      const startVMResponse = await postStartVM(
        this.props.auth,
        this.props.auth.sandboxId,
        vmProfile
      );

      if (startVMResponse.status !== 'ok') throw new Error('Could not load VM');
      this.loadVM();
    } catch (err: any) {
      this.setState(
        {
          loadingVM: false,
          startingVM: false,
        },
        () =>
          this.props.alerts.setMessage(
            err instanceof Error ? { error: err.message } : err,
            'danger',
            true
          )
      );
    }
  }

  async shutdownVM() {
    if (window.confirm('Are you sure to shut down the virtual machine?')) {
      this.setState({
        shuttingDownVM: true,
        startingVM: false,
        vmOutdated: false,
        runningVM: false,
      });
      try {
        const shutdownVMResponse = await putShutdownVM(
          this.props.auth,
          this.props.auth.sandboxId
        );

        if (shutdownVMResponse.status === 'ok') {
          this.setState(
            {
              vmStatus: '',
              shuttingDownVM: false,
            },
            () => {
              this.fetchAvailableVMTypes();
              this.fetchVMRestrictions();
              this.fetchAvailableAcceleratorTypes();
              this.fetchVMDiskLimits();
            }
          );
          this.props.navigate(`/${this.props.auth.sandboxId}/vm`, {
            replace: true,
          });
        } else {
          throw new Error(
            'Could not shut down current Virtual Machine. Contact your administrator.'
          );
        }
      } catch (err: any) {
        this.setState(
          {
            vmStatus: '',
            shuttingDownVM: false,
            loadingVM: false,
          },
          () => {
            this.props.alerts.setMessage(
              err instanceof Error ? { error: err.message } : err,
              'danger',
              true
            );
            this.fetchAvailableVMTypes();
            this.fetchVMRestrictions();
            this.fetchAvailableAcceleratorTypes();
            this.fetchVMDiskLimits();
          }
        );
        this.props.navigate(`/${this.props.auth.sandboxId}/vm`, {
          replace: true,
        });
      }
    }
  }

  onMachineFamilyChange(event: any): void {
    this.setState({
      machineFamilyDropdownValue: event.target.value,
      machineSubtypeOptions: [],
      machineSubtypeDropdownValue: '',
      machineTypeOptions: [],
      machineTypeDropdownValue: '',
      includeAccelerator: false,
      acceleratorTypeDropdownValue: '',
      acceleratorSliderValue: 0,
    });
    this.fetchAvailableMachineTypes(event.target.value);
  }

  setMachineTypeOptions(
    machineTypes: VMMachineType[],
    subtypeFilter: string
  ): void {
    const options: CustomVMDropdownOption[] = [];
    const cpuSortedMachineTypes = machineTypes;
    cpuSortedMachineTypes.sort((a, b) =>
      a.quest_cpus > b.quest_cpus ? 1 : -1
    );
    for (const machineType of cpuSortedMachineTypes) {
      if (this.getMachineSubtype(machineType.name) === subtypeFilter) {
        options.push({
          name: machineType.description,
          value: machineType.name,
        });
      }
    }
    this.setState({
      machineTypeOptions: options,
    });
  }

  onMachineSubtypeChange(event: any): void {
    this.setMachineTypeOptions(
      this.state.availableMachineTypes,
      event.target.value
    );
    this.setState({
      machineSubtypeDropdownValue: event.target.value,
      machineTypeDropdownValue: '',
      acceleratorSliderValue: 0,
    });
  }

  onMachineTypeChange(event: any): void {
    this.setState({
      machineTypeDropdownValue: event.target.value,
      acceleratorSliderValue: 0,
    });
  }

  onAcceleratorTypeChange(event: any): void {
    this.setState({
      acceleratorTypeDropdownValue: event.target.value,
      acceleratorSliderValue: 0,
    });
  }

  getSelectedMachineType(): VMMachineType | undefined {
    if (this.state.machineTypeDropdownValue === '') {
      return undefined;
    }
    return this.state.availableMachineTypes.find(
      machineType => machineType.name === this.state.machineTypeDropdownValue
    );
  }

  getSelectedAcceleratorType(): VMAcceleratorType | undefined {
    if (this.state.acceleratorTypeDropdownValue === '') {
      return undefined;
    }
    return this.state.availableAcceleratorTypes.find(
      acceleratorType =>
        acceleratorType.name === this.state.acceleratorTypeDropdownValue
    );
  }

  getMaximumNumberOfAccelerators(): number {
    const machineType = this.getSelectedMachineType();
    const acceleratorType = this.getSelectedAcceleratorType();
    if (!machineType || !acceleratorType) {
      return 0;
    }
    let max = acceleratorType.maximum_cards_per_instance;
    if (max > this.state.maxAllowedAccelerators) {
      max = this.state.maxAllowedAccelerators;
    }
    return max;
  }

  renderShutdownButton() {
    return (
      <Button
        key="shutdown"
        type="button"
        onClick={() => {
          clearTimeout(this.vmTimeout);
          this.shutdownVM();
        }}
      >
        Shutdown VM
      </Button>
    );
  }

  renderOutdatedContinueButton() {
    return (
      <Button
        appendStyles={GrayButtonStyles}
        key="continue"
        type="button"
        onClick={() => {
          this.setState({
            outdatedConfirmed: true,
          });
        }}
      >
        Not now
      </Button>
    );
  }

  renderBypassButton() {
    return (
      <Button
        appendStyles={RedButtonStyles}
        key="bypass"
        type="button"
        onClick={() => {
          this.setState({
            bypassConfirmed: true,
          });
        }}
      >
        Bypass lockdown
      </Button>
    );
  }

  renderCheckStatusButton() {
    return (
      <Button
        key="reload"
        type="button"
        onClick={() => {
          this.setState({
            loadingVM: true,
          });
          this.loadVM();
        }}
      >
        Check status
      </Button>
    );
  }

  renderVNC() {
    const notifications: string[] = [];
    if (this.state.lockdownActive) {
      notifications.push('Lockdown Active');
    }
    if (this.state.vmOutdated) {
      notifications.push('New VM version available. Restart when convenient');
    }

    return (
      <>
        <VMNotification messages={notifications} />
        <NoVNC
          address={this.state.vmAddress}
          password={this.state.vmPassword}
          shuttingDownVM={this.state.shuttingDownVM}
          onDirtyDisconnect={this.onDirtyDisconnect}
          loadVM={this.loadVM}
          shutdownVM={this.shutdownVM}
          runningVM={this.state.runningVM}
        >
          {this.state.shuttingDownVM && (
            <PageLoader additionalText={'Closing virtual machine connection'} />
          )}
        </NoVNC>
      </>
    );
  }

  renderVMStarting() {
    return (
      <StyledVMStarting>
        <p>
          <strong>Machine is starting</strong>
        </p>
        <LoadingDots>
          {this.state.vmStatus !== 'RUNNING_VNC_NOT_READY' && (
            <span>
              <p>
                Your machine will be ready in a moment and you&apos;ll be
                connected to it...
              </p>
              <p>
                For technical reasons your connection will timeout every hour
                and you will need to reconnect
                <br />
                (but no changes will happen to your working environment).
              </p>
              <p>
                If your machine is not being used it will be automatically shut
                down after 24 hours
                <br />
                and any applications you have open will be closed.
              </p>
            </span>
          )}
          {this.state.vmStatus === 'RUNNING_VNC_NOT_READY' && (
            <span>Virtual Machine ready. Finalizing connection</span>
          )}
        </LoadingDots>
        {this.state.vmStatus === 'RUNNING_VNC_NOT_READY' && (
          <StyledReconnectText>
            If this takes more than two minutes please
            <StyledShutdownButton
              type="button"
              onClick={() => {
                clearTimeout(this.vmTimeout);
                this.shutdownVM();
              }}
            >
              shutdown
            </StyledShutdownButton>
            and restart the virtual machine.
          </StyledReconnectText>
        )}
      </StyledVMStarting>
    );
  }

  renderMaintenance() {
    return (
      <ContentArea>
        <StyledReconnectContainer>
          <StyledReconnectTitle>Service Under Maintenance</StyledReconnectTitle>
          {this.state.lockdownMessage !== '' && (
            <StyledReconnectDescription>
              {this.state.lockdownMessage}
            </StyledReconnectDescription>
          )}
          <StyledReconnectOptionContainer>
            {this.state.lockdownBypassAllowed && this.renderBypassButton()}
            {this.renderCheckStatusButton()}
            {this.state.vmStatus === 'RUNNING' && this.renderShutdownButton()}
          </StyledReconnectOptionContainer>
        </StyledReconnectContainer>
      </ContentArea>
    );
  }

  renderUpdateNeeded() {
    return (
      <ContentArea>
        <StyledReconnectContainer>
          <StyledReconnectTitle>Update Virtual Machine</StyledReconnectTitle>
          <StyledReconnectDescription>
            New VM version available. Restart when convenient. Some applications
            and services might not work with an outdated VM.
          </StyledReconnectDescription>
          <StyledReconnectOptionContainer>
            {this.renderOutdatedContinueButton()}
            {this.renderShutdownButton()}
          </StyledReconnectOptionContainer>
        </StyledReconnectContainer>
      </ContentArea>
    );
  }

  getVmCost(pricePerHour: number, currency: string) {
    let currencySign = '';
    switch (currency) {
      case 'EUR':
        currencySign = '€';
        break;
      case 'GBP':
        currencySign = '£';
        break;
      case 'USD':
        currencySign = '$';
        break;
    }

    const daysInMonth = 30;
    const pricePerMonth = (pricePerHour * 24 * daysInMonth).toFixed(0);

    return `Estimated cost: ${pricePerMonth} ${currencySign} / month`;
  }

  renderStandardVmCost(vmType: VMType) {
    if (!vmType || vmType.price_per_hour === 0) {
      return null;
    }

    return (
      <li key="price">
        {this.getVmCost(vmType.price_per_hour, vmType.currency)}
      </li>
    );
  }

  renderSelectedCustomVmCost() {
    const machineType = this.getSelectedMachineType();
    if (!machineType || machineType.price_per_hour === 0) {
      return null;
    }

    return (
      <StyledCustomVMPrice>
        {this.getVmCost(machineType.price_per_hour, machineType.currency)}
      </StyledCustomVMPrice>
    );
  }

  renderSelectProfile() {
    return (
      <>
        <StyledParagraph>Select your virtual machine type</StyledParagraph>
        <StyledVirtualMachineSelector>
          {this.state.vmTypes.map((vmType: VMType) => (
            <StyledVirtualMachineCard key={vmType.id}>
              <StyledVMName>{vmType.name}</StyledVMName>
              <StyledVMProperties>
                {vmType.description &&
                  JSON.parse(vmType.description).map((b: string) => (
                    <li key={b}>{b}</li>
                  ))}
                {this.renderStandardVmCost(vmType)}
              </StyledVMProperties>
              <StyledStartVMButton
                variant="secondary"
                onClick={() =>
                  this.startVM({
                    profile_id: vmType.id,
                    disk_size: this.state.homeDiskSizeNewGB,
                  })
                }
              >
                Start machine
              </StyledStartVMButton>
            </StyledVirtualMachineCard>
          ))}
          {this.state.machineFamilyOptions.length > 0 && (
            <StyledCustomVirtualMachineCard>
              <StyledCustomVMButton
                ref={this.customVMProfileRef}
                onClick={() => {
                  this.setState({
                    showCustomVMProfile: !this.state.showCustomVMProfile,
                  });
                  if (this.customVMProfileRef.current !== null) {
                    this.customVMProfileRef.current.scrollIntoView();
                  }
                }}
              >
                Use custom Virtual Machine profile
                <StyledAccordionChevron
                  size={18}
                  aria-hidden={true}
                  $isOpen={this.state.showCustomVMProfile}
                />
              </StyledCustomVMButton>
              {this.state.showCustomVMProfile && (
                <StyledAccordionContent>
                  <StyledCustomVMProperties>
                    <StyledCustomVMSection>
                      <CustomVMDropdown
                        name="machineFamily"
                        labelText="Machine Family"
                        value={this.state.machineFamilyDropdownValue}
                        options={this.state.machineFamilyOptions}
                        emptyText="No available families"
                        placeholderText="Please select family..."
                        disabled={this.state.machineFamilyOptions.length === 0}
                        onChange={(event: any) => {
                          this.onMachineFamilyChange(event);
                        }}
                      />
                    </StyledCustomVMSection>
                    <StyledCustomVMSection>
                      <CustomVMDropdown
                        name="Subtype"
                        labelText="Family Subtype"
                        value={this.state.machineSubtypeDropdownValue}
                        options={this.state.machineSubtypeOptions}
                        emptyText="No available subtypes"
                        placeholderText="Please select subtype..."
                        disabled={this.state.machineSubtypeOptions.length === 0}
                        onChange={(event: any) => {
                          this.onMachineSubtypeChange(event);
                        }}
                      />
                    </StyledCustomVMSection>
                    <StyledCustomVMSection>
                      <CustomVMDropdown
                        name="machineType"
                        labelText="Machine Type"
                        value={this.state.machineTypeDropdownValue}
                        options={this.state.machineTypeOptions}
                        emptyText="No available types"
                        placeholderText="Please select type..."
                        disabled={this.state.machineTypeOptions.length === 0}
                        onChange={(event: any) => {
                          this.onMachineTypeChange(event);
                        }}
                      />
                    </StyledCustomVMSection>
                    {this.state.acceleratorTypeOptions.length > 0 &&
                      this.state.maxAllowedAccelerators > 0 && (
                        <StyledCustomVMSection>
                          <CustomVMCheckbox
                            labelText="Include GPUs"
                            notAvailableText={
                              this.state.machineFamilyDropdownValue !== 'N1' &&
                              this.state.machineFamilyDropdownValue !== ''
                                ? 'Not available for selected family'
                                : ''
                            }
                            checked={this.state.includeAccelerator}
                            onChange={(e: any) =>
                              this.setState({
                                includeAccelerator: e.target.checked,
                              })
                            }
                            disabled={
                              this.state.machineFamilyDropdownValue !== 'N1' ||
                              this.state.machineTypeDropdownValue === ''
                            }
                          ></CustomVMCheckbox>
                          {this.state.includeAccelerator && (
                            <>
                              <CustomVMDropdown
                                name="acceleratorType"
                                labelText="GPU type"
                                value={this.state.acceleratorTypeDropdownValue}
                                options={this.state.acceleratorTypeOptions}
                                emptyText="No available GPUs"
                                placeholderText="Please select a GPU..."
                                disabled={!this.state.includeAccelerator}
                                onChange={(event: any) => {
                                  this.onAcceleratorTypeChange(event);
                                }}
                              />
                              <CustomVMSlider
                                name="acceleratorSlider"
                                labelText="GPUs"
                                min="0"
                                max={this.getMaximumNumberOfAccelerators()}
                                value={this.state.acceleratorSliderValue}
                                disabled={
                                  this.state.acceleratorTypeDropdownValue === ''
                                }
                                onChange={(event: any) => {
                                  this.setState({
                                    acceleratorSliderValue: Number(
                                      event.target.value
                                    ),
                                  });
                                }}
                              />
                            </>
                          )}
                        </StyledCustomVMSection>
                      )}
                  </StyledCustomVMProperties>
                  {this.renderSelectedCustomVmCost()}
                  <StyledStartVMButton
                    variant="secondary"
                    disabled={this.state.machineTypeDropdownValue === ''}
                    onClick={() =>
                      this.startVM({
                        min_cpu_platform: '',
                        machine_type: this.state.machineTypeDropdownValue,
                        gpu_count: this.state.includeAccelerator
                          ? this.state.acceleratorSliderValue
                          : 0,
                        gpu_type: this.state.includeAccelerator
                          ? this.state.acceleratorTypeDropdownValue
                          : '',
                        disk_size: this.state.homeDiskSizeNewGB,
                      })
                    }
                  >
                    Start machine
                  </StyledStartVMButton>
                </StyledAccordionContent>
              )}
            </StyledCustomVirtualMachineCard>
          )}

          <StyledCustomVirtualMachineCard>
            <StyledCustomVMButton
              ref={this.resizeHomeDiskRef}
              onClick={() => {
                this.setState({
                  resizeHomeDisk: !this.state.resizeHomeDisk,
                });
                if (this.resizeHomeDiskRef.current !== null) {
                  this.resizeHomeDiskRef.current.scrollIntoView();
                }
              }}
            >
              Resize home disk
              <StyledAccordionChevron
                size={18}
                aria-hidden={true}
                $isOpen={this.state.resizeHomeDisk}
              />
            </StyledCustomVMButton>
            {this.state.resizeHomeDisk && (
              <StyledAccordionContent>
                <StyledParagraph>
                  Please note that you can&apos;t revert this change or reduce
                  your disk size.
                </StyledParagraph>
                <StyledCustomVMProperties>
                  <CustomVMSlider
                    name="homeDiskSizeSlider"
                    labelText="Home disk size (GB)"
                    min={this.state.homeDiskSizeMinGB}
                    max={this.state.homeDiskSizeMaxGB}
                    value={this.state.homeDiskSizeNewGB}
                    step="10"
                    disabled={
                      this.state.homeDiskSizeOriginalGB ===
                      this.state.homeDiskSizeMaxGB
                    }
                    onChange={(event: any) => {
                      this.setState({
                        homeDiskSizeNewGB: Number(event.target.value),
                      });
                    }}
                  />
                </StyledCustomVMProperties>
                {this.state.homeDiskSizeOriginalGB ===
                  this.state.homeDiskSizeMaxGB && (
                  <>
                    <StyledParagraph>
                      You are already using biggest disk available.
                    </StyledParagraph>
                  </>
                )}
              </StyledAccordionContent>
            )}
          </StyledCustomVirtualMachineCard>
        </StyledVirtualMachineSelector>
      </>
    );
  }

  render() {
    if (this.state.shuttingDownVM) {
      return (
        <VirtualMachineControlPanel>
          <LoadingDots>Closing connection, please wait</LoadingDots>
        </VirtualMachineControlPanel>
      );
    }

    if (this.state.loadingVM) {
      return (
        <VirtualMachineControlPanel>
          <LoadingDots>Checking virtual machine status</LoadingDots>
          <StyledReconnectText>
            If this takes more than two minutes please
            <StyledShutdownButton
              type="button"
              onClick={() => {
                clearTimeout(this.vmTimeout);
                this.shutdownVM();
              }}
            >
              shutdown
            </StyledShutdownButton>
            and restart the virtual machine.
          </StyledReconnectText>
        </VirtualMachineControlPanel>
      );
    }

    if (this.state.startingVM) {
      return (
        <VirtualMachineControlPanel>
          {this.renderVMStarting()}
        </VirtualMachineControlPanel>
      );
    }

    if (this.state.lockdownActive && !this.state.bypassConfirmed) {
      return (
        <VirtualMachineControlPanel>
          {this.renderMaintenance()}
        </VirtualMachineControlPanel>
      );
    }

    if (
      this.state.vmStatus !== 'STOPPING' &&
      this.state.runningVM &&
      this.state.vmOutdated &&
      !this.state.outdatedConfirmed
    ) {
      return (
        <VirtualMachineControlPanel>
          {this.renderUpdateNeeded()}
        </VirtualMachineControlPanel>
      );
    }

    if (this.state.vmStatus === 'RUNNING' && this.state.vmAddress !== '') {
      return this.renderVNC();
    }

    return (
      <VirtualMachineControlPanel>
        {this.renderSelectProfile()}
      </VirtualMachineControlPanel>
    );
  }
}

export default withAuth(VirtualMachinePage);
